import type { EngineProfile, MessageRecord, ThreadRecord } from "@/runtime/events";

import { getAllRecords, putRecord, runTransaction } from "./db";

function createId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export async function listThreads(): Promise<ThreadRecord[]> {
  const records = await getAllRecords<ThreadRecord>("threads");
  return records.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export async function createThread(profile: EngineProfile): Promise<ThreadRecord> {
  const timestamp = new Date().toISOString();
  const record: ThreadRecord = {
    id: createId("thread"),
    title: "New Thread",
    createdAt: timestamp,
    updatedAt: timestamp,
    profile,
  };

  await putRecord("threads", record);
  return record;
}

export async function renameThread(thread: ThreadRecord, title: string): Promise<ThreadRecord> {
  const nextThread = {
    ...thread,
    title,
    updatedAt: new Date().toISOString(),
  };

  await putRecord("threads", nextThread);
  return nextThread;
}

export async function updateThreadProfile(
  thread: ThreadRecord,
  profile: EngineProfile,
): Promise<ThreadRecord> {
  const nextThread = {
    ...thread,
    profile,
    updatedAt: new Date().toISOString(),
  };

  await putRecord("threads", nextThread);
  return nextThread;
}

export async function listMessages(threadId: string): Promise<MessageRecord[]> {
  const db = await runTransaction("messages", "readonly", async (store) => {
    const index = store.index("threadId");
    return new Promise<MessageRecord[]>((resolve, reject) => {
      const request = index.getAll(threadId);
      request.onsuccess = () => resolve(request.result as MessageRecord[]);
      request.onerror = () => reject(request.error);
    });
  });

  return db.sort((left, right) => left.createdAt.localeCompare(right.createdAt));
}

export async function appendMessage(
  thread: ThreadRecord,
  role: MessageRecord["role"],
  text: string,
): Promise<MessageRecord> {
  const timestamp = new Date().toISOString();
  const message: MessageRecord = {
    id: createId("message"),
    threadId: thread.id,
    role,
    text,
    createdAt: timestamp,
  };

  await putRecord("messages", message);
  await putRecord("threads", {
    ...thread,
    updatedAt: timestamp,
    title:
      thread.title === "New Thread" && role === "user"
        ? text.slice(0, 48) || thread.title
        : thread.title,
  });

  return message;
}
